import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { HelpSlides } from "src/model/class/general classes/help-slides";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { clientSettingsUpdateHelperAction } from "src/state/actions/client-settings.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";

export enum helpMessagesID {
  ReorderElements = 0,
  LoadControl = 1,
}

@Injectable({
  providedIn: "root",
})
export class HelperService implements OnInit, OnDestroy {
  /**
   * To use a new message with the helper service:
   *  - Add an anum in the helMessageID
   *  - On the click event button where you want to show the message you add
   *      this.helperMessageService.showHelpMessage(helpMessagesID.NewMessageID);
   *  - In the HelpSlideComponent.ShowHelpInfo function add the translation of the messages
   */

  private _logContext: string = "Helper.service";
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectCurrentUserSub: Subscription = null;
  private _selectClientSettingsUserSub: Subscription = null;
  //#endregion

  private currentUser: CurrentSessionData;
  private _helperMap: Map<number, string> = new Map<number, string>();

  private _currentClientSetting: ClientSettings = null;

  public Slides: HelpSlides = new HelpSlides();

  slideOpts = {
    initialSlide: 1,
    speed: 400,
  };

  constructor(
    private _modalCtrl: ModalController,
    private _evtService: EventService,
    private _store: Store,
    private _loggerService: LoggerService
  ) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "CONSTRUCTOR()",
      "GO!!"
    );
    this.subscribeSettingServiceData();
    this.ngOnInit();
  }

  /**
   * Handle the current user, because the help message are showed on the basis of the current logged user
   */
  ngOnInit() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnInit()",
      "INIT"
    );
    this.loadCurrentUser();

    this._evtService.OnMakeLogout.subscribe(() => {
      this.currentUser = null;
    });
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();

    this._evtService.OnMakeLogout.unsubscribe();
  }

  /**
   * Receive the message to show and check if it needs to show it:
   *  we show the message only if the user hasn't seen that message already,
   *  or it has seen that message but it has clicked "Show me again".
   *
   * @param helpMessage ID of the message to show
   */
  async showHelpMessage(component, helpMessage: helpMessagesID) {
    if (this.currentUser && this.currentUser != null) {
      if (
        this._currentClientSetting.helpAlreadyViewForUser &&
        this._currentClientSetting.helpAlreadyViewForUser.size > 0
      ) {
        if (
          this._currentClientSetting.helpAlreadyViewForUser.has(
            this.currentUser.id
          )
        ) {
          const helperViewed = this._currentClientSetting.helpAlreadyViewForUser
            .get(this.currentUser.id)
            .split(",");

          if (!helperViewed.includes(helpMessage.toString())) {
            this.ShowHelpInfo(component, helpMessage);
          }
        }
      } else {
        this.ShowHelpInfo(component, helpMessage);
      }
    }
  }

  public async ExitSlides(showAgain: boolean, helpMessage: helpMessagesID) {
    if (!showAgain) {
      if (this.currentUser) {
        if (
          this._currentClientSetting.helpAlreadyViewForUser &&
          this._currentClientSetting.helpAlreadyViewForUser.size > 0
        ) {
          if (
            this._currentClientSetting.helpAlreadyViewForUser.has(
              this.currentUser.id
            )
          ) {
            const helperViewed =
              this._currentClientSetting.helpAlreadyViewForUser
                .get(this.currentUser.id)
                .split(",");

            if (!helperViewed.includes(helpMessage.toString())) {
              this._currentClientSetting.helpAlreadyViewForUser.set(
                this.currentUser.id,
                this._currentClientSetting.helpAlreadyViewForUser.get(
                  this.currentUser.id
                ) +
                "," +
                helpMessage
              );
            }
          } else {
            this._currentClientSetting.helpAlreadyViewForUser.set(
              this.currentUser.id,
              helpMessage.toString()
            );
          }
        } else {
          this._currentClientSetting.helpAlreadyViewForUser = new Map<
            number,
            string
          >();
          this._currentClientSetting.helpAlreadyViewForUser.set(
            this.currentUser.id,
            helpMessage.toString()
          );
        }

        this._store.dispatch(
          clientSettingsUpdateHelperAction({
            helpers: this._currentClientSetting.helpAlreadyViewForUser,
          })
        );
      }
    }
    await this._modalCtrl.dismiss({});
  }

  private async ShowHelpInfo(component, helpMessage: helpMessagesID) {
    const modal = await this._modalCtrl.create({
      component: component,
      componentProps: {
        HelpMessageID: helpMessage,
      },
    });

    modal.present();
    const { data } = await modal.onWillDismiss();
    if (modal) {
      modal.dismiss();
    }

    if (data) {
      this.ExitSlides(data.showAgain, helpMessage);
    }
  }

  /**
   * Load current user data
   */
  private loadCurrentUser(): void {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe(async (currentUser: CurrentSessionData) => {
        this.currentUser = currentUser;
      });
  }

  /**
   * Subsribe to public Observable "clientSettings" of "settings.service", which propagates the info about the general app
   * -related settings as defined for this device; it will also emit each time these settings are changed.
   */
  private subscribeSettingServiceData(): void {
    this._selectClientSettingsUserSub = this._store
      .select(clientSettings)
      .pipe(filter((clientSetting) => clientSetting !== null))
      .subscribe((clientSettings: ClientSettings) => {
        this._currentClientSetting = clientSettings;
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectCurrentUserSub) {
      this._selectCurrentUserSub.unsubscribe();
    }

    if (this._selectClientSettingsUserSub) {
      this._selectClientSettingsUserSub.unsubscribe();
    }
  }
  //#endregion
}
